<script setup>
import { ref, onMounted, onUnmounted, nextTick, inject } from 'vue'
import Sequence from './Sequence.vue'
import IconButton from '@/components/buttons/IconButton.vue'
import IconPlus from '@/components/icons/IconPlus.vue'
import IconMinus from '@/components/icons/IconMinus.vue'
import IconChevron from '@/components/icons/IconChevron.vue'
import IconReset from '@/components/icons/IconReset.vue'

import useInteraction from '@/composables/interaction'

const props = defineProps({
  manifestUrl: {
    type: String,
    default: 'https://data.getty.edu/media/manifest/bayard-custom'
  },
  canvasIndex: {
    type: Number,
    default: 0
  },
  isZoomable: {
    type: Boolean,
    required: false,
    default: true,
  },
  overlayControls: {
    type: Boolean,
    required: false,
    default: false,
  },
  eventsToHandle: {
    type: Array,
    required: false,
    default: () => [
      "wheel",
      "touchstart",
      "touchend",
      "touchmove",
      "touchcancel",
    ],
  },
  thumbnails: {
    type: Boolean,
    default: false
  }
})

const { setThumbnails, addThumbnails } = inject('thumbnails', {
  setThumbnails: () => {},
  addThumbnails: () => {}
})

const sequencePanel = ref(null)

// let manifest = undefined

// const disableZoomIn = ref(false)
// const disableZoomOut = ref(true)

const currentSequence = ref(undefined)
const disableNext = ref(false)
const disablePrev = ref(true)


// Zoom Handlers
const zoomIn = () => {
  if (sequencePanel.value.panel) {
    sequencePanel.value.panel.zoomIn()
    useInteraction('iiif_canvas_zoom_in', {
      canvas: sequencePanel.value.panel.sequence.currentSequenceIndex || undefined
    })
  }
}

const zoomOut = () => {
  if (sequencePanel.value.panel) {
    sequencePanel.value.panel.zoomOut()
    useInteraction('iiif_canvas_zoom_out', {
      canvas: sequencePanel.value.panel.sequence.currentSequenceIndex || undefined
    })
  }
}

const goHome = () => {
  if (sequencePanel.value.panel) {
    sequencePanel.value.panel.goHome()
    useInteraction('iiif_canvas_reset_to_home', {
      canvas: sequencePanel.value.panel.sequence.currentSequenceIndex || undefined
    })
  }
}

//Sequence Handlers
const nextCanvas = () => {
  if (sequencePanel.value.panel) {
    sequencePanel.value.panel.sequence.nextCanvas()
    useInteraction('iiif_canvas_next', {
      canvas: sequencePanel.value.panel.sequence.currentSequenceIndex || undefined
    })
  }
}

const prevCanvas = () => {
  if (sequencePanel.value.panel) {
    sequencePanel.value.panel.sequence.previousCanvas()
    useInteraction('iiif_canvas_previous', {
      canvas: sequencePanel.value.panel.sequence.currentSequenceIndex || undefined
    })
  }
}

//Thumbnails
const getThumbnails = async () => {
  if (!props.thumbnails) { return }

  let thumbnails = []

  if (sequencePanel.value && sequencePanel.value.manifest) {
    console.log('getting tbs')
    thumbnails = await Promise.all(
      sequencePanel.value.panel.sequence.sequence.slice(0,5).map(
        async (sequence) => {
          const spread = await Promise.all(
            sequence.map(async (itemIndex) => {
              const canvas = sequencePanel.value.manifest.items[itemIndex]
              const item = await sequencePanel.value.panel.vault.get(canvas.id)
              // const thumb = await sequencePanel.value.panel.getThumbnail(item)

              const thumb = {
                id: `https://static.getty.edu/collection-online-20/bayard-manifest/bayard_thumb_${itemIndex}.jpg`
              }

              return {
                canvasIndex: itemIndex,
                id: canvas.id,
                thumbnail: thumb,
                label: item.label
              }
            })
          )
          return spread
        }
      )
    )

    setThumbnails(thumbnails)
    console.log('initial tbs set')

    const restOfTheSequences = sequencePanel.value.panel.sequence.sequence.slice(5)

    for(const sequence of restOfTheSequences) {
      const spread = await Promise.all(
        sequence.map(async (itemIndex) => {
          const canvas = sequencePanel.value.manifest.items[itemIndex]
          const item = await sequencePanel.value.panel.vault.get(canvas.id)

          // const thumb = await sequencePanel.value.panel.getThumbnail(item)

          const thumb = {
            id: `https://static.getty.edu/collection-online-20/bayard-manifest/bayard_thumb_${itemIndex}.jpg`
          }

          return {
            canvasIndex: itemIndex,
            id: canvas.id,
            thumbnail: thumb,
            label: item.label
          }
        })
      )

      addThumbnails([spread])
    }
  }
}


//Event Handlers

const interactionEventHandler = (event) => {
  if (
    (event.type == "wheel" && !event.metaKey) ||
    (event.touches && event.touches.length < 2)
  ) {
    event.stopPropagation()
    return false
  }
}

const setEventListeners = (el) => {
  // //TODO: I may just remove the zoom disabling listeners
  // el.addEventListener("zoom", (e) => {
  //   disableZoomIn.value = e.detail.isMax

  //   // isMin is not reliable; temporary? fix for determining whether to disableZoomOut
  //   disableZoomOut.value = e.detail.isMin
  // })

  el.addEventListener("sequence-change", (e) => {
    currentSequence.value = e.currentSequence
    disablePrev.value = e.detail.index <= 0
    disableNext.value = e.detail.index >= e.detail.total - 1
  })

  el.addEventListener("mousedown", () => {
    useInteraction('iiif_drag')
  })
}

const setInteractionEventListeners = (el) => {
  const options = { passive: true, capture: true }

  for (const eventType of props.eventsToHandle) {
    el.addEventListener(eventType, interactionEventHandler, options);
  }
}

//Lifecycles

onMounted(async () => {
  await nextTick()
  const panelEl = sequencePanel.value.panel
  setEventListeners(panelEl)
  setInteractionEventListeners(panelEl)
})

onUnmounted(() => {
  props.eventsToHandle.forEach((e) => {
    document.removeEventListener(e, interactionEventHandler)
  })
})

</script>

<template>
  <div id="image-viewer" class="relative">
    <Sequence
      ref="sequencePanel"
      :manifest-url="manifestUrl"
      :canvas-index="canvasIndex"
      @viewer-ready.once="getThumbnails"
      class="flex flex-col bg-dark-grey"
    >
      <template #controls>
        <div id="image-viewer-controls" class="absolute bottom-0 left-0 z-20 w-full max-h-full flex flex-col">
          <div id="button-controls" class="p-8 fill-cream">
            <div
              v-if="overlayControls"
              id="nav-controls"
              class="flex flex-row justify-between pb-10"
            >
              <IconButton :disabled="disablePrev" @click="prevCanvas" class="py-[20px]">
                <IconChevron class="rotate-180 w-25"/>
              </IconButton>
              <IconButton :disabled="disableNext" @click="nextCanvas" class="py-[20px]">
                <IconChevron class="w-25"/>
              </IconButton>
            </div>
            <div class="flex flex-row justify-between">
              <div v-if="isZoomable" id="zoom-controls" class="flex gap-x-[20px]">
                <IconButton @click="goHome">
                  <IconReset class="w-25 h-25 scale-150" />
                </IconButton>
                <IconButton @click="zoomIn">
                  <IconPlus class="w-25 h-25"/>
                </IconButton>
                <IconButton @click="zoomOut">
                  <IconMinus class="w-25 h-25"/>
                </IconButton>
              </div>
              <div>
                <slot name="menuControls" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </Sequence>
  </div>
</template>

<style>
#image-viewer button {
  box-shadow: 4px 4px 4px rgb(0 0 0 / 25%);
}

#image-viewer button[disabled] {
  opacity: 75%;
  background: #535348;
}
</style>
